<template>
	<div class="event-center">
		<div
			v-if="eventCenterDisabled"
			class="event-center__disabled"
		>
			Event Center is disabled for this context.
		</div>

		<template v-else>
			<div
				v-if="isLoadingNavigation"
				class="event-center__loading"
			>
				<LoadingSpinner />
			</div>

			<template v-else>
				<EventCenterNavigation
					:show-scope-tabs="showScopeTabs"
					:navigation-config="navigationConfig"
					:is-competition-view="isCompetitionView"
					:show-search="showSearch"
					:show-select-filters="showSelectFilters"
					:query-state="queryState"
					:scope-nav-items="scopeNavItems"
					:all-events-nav-items="allEventsNavItems"
					:competition-options="competitionOptions"
					:competition-content-nav-items="competitionContentNavItems"
					:sub-stage-options="subStageOptions"
					:sub-stage-value="subStageValue"
					:active-filters="activeFilters"
					@scope-change="$emit('scope-change', $event)"
					@competition-change="$emit('competition-change', $event)"
					@competition-subview-change="$emit('competition-subview-change', $event)"
					@competition-content-change="$emit('competition-content-change', $event)"
					@substage-change="$emit('substage-change', $event)"
					@all-events-view-change="$emit('all-events-view-change', $event)"
					@search-change="$emit('search-change', $event)"
					@filter-change="$emit('filter-change', $event)"
				/>

				<div
					v-if="showMatchDays"
					class="event-center__results"
				>
					<EventCenterEventList
						ref="matchDays"
						:events="displayEvents"
						:is-loading="isLoadingEvents"
						:view-mode="eventListViewMode"
						:context-type="contextType"
						:competition-selected="isCompetitionView"
						:selected-stage="selectedStage"
						:selected-sub-stage-id="selectedSubStageId"
						:has-active-search-or-filter="hasActiveSearchOrFilter"
						:active-filters="activeFilters"
					/>
				</div>

				<div
					v-if="isCompetitionView && !showMatchDays"
					class="event-center__results"
				>
					<div
						v-if="competitionContentView === 'table' && isTableNotFound"
						class="event-center__not-found"
					>
						Not found
					</div>
					<LeagueTable
						v-if="competitionContentView === 'table' && !isTableNotFound"
						:table="leagueTable"
						:is-loading="isLoadingTable"
					/>
					<div
						v-if="competitionContentView === 'top-scorer' && isTopScorerNotFound"
						class="event-center__not-found"
					>
						Not found
					</div>
					<TopScorers
						v-if="competitionContentView === 'top-scorer' && !isTopScorerNotFound"
						:top-scorer-tables="topScorerTables"
						:is-loading="isLoadingTopScorers"
					/>
				</div>

				<Pagination
					v-if="shouldShowPagination"
					:total="pagination.total"
					:per-page="pagination.perPage"
					:current-page="pagination.currentPage"
					:last-page="pagination.lastPage"
					:dark-theme="!$arenaConfig.isLightTheme"
					@change="$emit('pagination-change', $event)"
				/>
			</template>
		</template>
	</div>
</template>

<script>
import EventCenterEventList from '@js/components/EventCenter/EventCenterEventList';
import LeagueTable from '@js/components/LeagueTable';
import TopScorers from '@js/components/TopScorers';
import EventCenterNavigation from '@js/components/EventCenter/EventCenterNavigation';
import Pagination from '@shared/components/Pagination';
import LoadingSpinner from '@shared/components/LoadingSpinner';

export default {
	name:       'EventCenterView',
	components: {
		EventCenterEventList,
		EventCenterNavigation,
		LeagueTable,
		LoadingSpinner,
		Pagination,
		TopScorers,
	},
	props: {
		eventCenterDisabled: {
			type:    Boolean,
			default: false,
		},
		showScopeTabs: {
			type:    Boolean,
			default: false,
		},
		isCompetitionView: {
			type:    Boolean,
			default: false,
		},
		isLoadingEvents: {
			type:    Boolean,
			default: false,
		},
		isLoadingNavigation: {
			type:    Boolean,
			default: false,
		},
		isLoadingTable: {
			type:    Boolean,
			default: false,
		},
		isLoadingTopScorers: {
			type:    Boolean,
			default: false,
		},
		isTableNotFound: {
			type:    Boolean,
			default: false,
		},
		isTopScorerNotFound: {
			type:    Boolean,
			default: false,
		},
		showSearch: {
			type:    Boolean,
			default: true,
		},
		showSelectFilters: {
			type:    Boolean,
			default: true,
		},
		showMatchDays: {
			type:    Boolean,
			default: true,
		},
		eventListViewMode: {
			type:     String,
			required: true,
		},
		contextType: {
			type:     String,
			required: true,
		},
		hasActiveSearchOrFilter: {
			type:    Boolean,
			default: false,
		},
		selectedStage: {
			type:    Object,
			default: null,
		},
		selectedSubStageId: {
			type:    [String, null],
			default: null,
		},
		displayEvents: {
			type:    Array,
			default: () => [],
		},
		leagueTable: {
			type:    Object,
			default: null,
		},
		topScorerTables: {
			type:    Array,
			default: null,
		},
		queryState: {
			type:     Object,
			required: true,
		},
		navigationConfig: {
			type:    Object,
			default: null,
		},
		scopeNavItems: {
			type:    Array,
			default: () => [],
		},
		allEventsNavItems: {
			type:    Array,
			default: () => [],
		},
		competitionOptions: {
			type:    Array,
			default: () => [],
		},
		competitionContentNavItems: {
			type:    Array,
			default: () => [],
		},
		subStageOptions: {
			type:    Array,
			default: () => [],
		},
		subStageValue: {
			type:    [String, Number, null],
			default: null,
		},
		activeFilters: {
			type:    Array,
			default: () => [],
		},
		pagination: {
			type:    Object,
			default: null,
		},
	},
	computed: {
		competitionContentView() {
			return this.competitionContentNavItems.find(item => item.active)?.url || 'fixtures-results';
		},
		shouldShowPagination() {
			return Number(this.pagination?.total) > 0;
		},
	},
	methods: {
		resetMatchdaysView() {
			this.$refs.matchDays?.setViewToMatchdays?.();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.event-center {
	padding-top: $sp6;
	margin-bottom: $sp7;
}

.event-center__disabled {
	padding: $sp4;
	border: 1px solid var(--color-grey-700);
	border-radius: $sp2;
	color: var(--color-grey-300);
	margin-bottom: $sp4;
}

.event-center__results {
	margin-bottom: $sp4;
}

.event-center__loading {
	min-height: 180px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: $sp4;
}

.event-center__not-found {
	padding: $sp4;
	border: 1px solid var(--color-grey-700);
	border-radius: $sp2;
}
</style>
